/**
 * Inline preview Office-вложений в MFileInput (WUI-317 + WUI-344 + WUI-492).
 * Lifecycle как у dialog: session + dirty→editing + close→awaiting_ws|restore.
 */
import { computed, nextTick, onUnmounted, ref, type Ref } from 'vue'
import type { FileItem } from '@/common/types/fileItem'
import type { useAttachmentR7Editor } from '@/common/composables/useAttachmentR7Editor'
import type { useFileAttachmentsElement } from '@/common/composables/useFileAttachmentsElement'
import { getDsAttachmentStore } from '@/common/composables/useDsAttachmentState'
import {
  isAttachmentR7PreviewAwaitingFinalSave,
  resolveAttachmentDisplayFileName,
} from '@/common/utils/attachmentRpcPolicy'
import {
  finalizeR7AttachmentEditorClose,
  markR7AttachmentDocumentDirty,
} from '@/common/utils/r7AttachmentEditorLifecycle'
import {
  getEditingSession,
  markEditorClosedKeepStaged,
} from '@/common/utils/r7AttachmentEditSession'
import { shouldDestroyR7EditorAfterFormSaveWait, waitUntilAttachmentPreviewReadyForFormSave, type R7FormSaveWaitResult } from '@/common/utils/r7AttachmentFormSaveWait'
import {
  attachmentItemsReferSameRow,
  attachmentUiRowKey,
  isNewLocalAttachmentItem,
  resolveAttachmentRowCodeVersion,
} from '@/common/utils/fileAttachmentsUniData'
import type { WebSocketService } from '@/services/websocket/websocket.service'

export type R7InlinePreviewOverlay = 'loading' | 'awaiting_ws' | 'editor_open'

export type R7AttachmentStagingUpdatedPayload = {
  code: number
  version: number
  storageUri: string
  documentKey?: string
}

export type UseR7InlinePreviewOptions = {
  previewHostId: string
  attachmentR7Preview: ReturnType<typeof useAttachmentR7Editor>
  fileAttachmentsElement: ReturnType<typeof useFileAttachmentsElement>
  wsService: WebSocketService
  isEditorVisible: Ref<boolean>
}

type ActivePreviewEditor = {
  item: FileItem
  elementId: number
  documentKey: string
}

export function useR7InlinePreview(options: UseR7InlinePreviewOptions) {
  const { previewHostId, attachmentR7Preview, fileAttachmentsElement, wsService, isEditorVisible } =
    options

  const previewLoading = ref(false)
  const previewError = ref<string | null>(null)
  const previewLastFocusFile = ref<FileItem | null>(null)
  let previewLoadGen = 0
  let previewFocusDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let previewRestoreDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let previewAbortController: AbortController | null = null
  let activePreviewEditor: ActivePreviewEditor | null = null
  let lastTapePreviewFocusKey = ''
  let lastTapePreviewFocusFile: FileItem | null = null
  let formSaveHoldPreview = false

  function abortInFlightPreview(reason = 'preview-cancelled'): void {
    previewAbortController?.abort(reason)
    previewAbortController = null
  }

  function bumpPreviewGeneration(): void {
    abortInFlightPreview()
    previewLoadGen++
  }

  function isAbortError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false
    const e = error as { name?: string; code?: string }
    return e.name === 'CanceledError' || e.name === 'AbortError' || e.code === 'ERR_CANCELED'
  }

  function resolvePreviewElementId(): number | undefined {
    return fileAttachmentsElement.resolveR7EditorOpenContext()?.elementId
  }

  function isPreviewAwaitingForFile(file: FileItem): boolean {
    return isAttachmentR7PreviewAwaitingFinalSave(file, resolvePreviewElementId())
  }

  const inlinePreviewOverlay = computed((): R7InlinePreviewOverlay | null => {
    if (isEditorVisible.value) return 'editor_open'
    const file = previewLastFocusFile.value
    if (file) {
      const resolved = fileAttachmentsElement.resolveAttachmentItemFromStore(file)
      if (isPreviewAwaitingForFile(resolved)) return 'awaiting_ws'
    }
    if (previewLoading.value) return 'loading'
    return null
  })

  function clearPreviewFocusDebounce(): void {
    if (previewFocusDebounceTimer != null) {
      clearTimeout(previewFocusDebounceTimer)
      previewFocusDebounceTimer = null
    }
  }

  function clearPreviewRestoreDebounce(): void {
    if (previewRestoreDebounceTimer != null) {
      clearTimeout(previewRestoreDebounceTimer)
      previewRestoreDebounceTimer = null
    }
  }

  function clearInlinePreviewHostDom(): void {
    const host = document.getElementById(previewHostId)
    if (host) {
      host.replaceChildren()
    }
  }

  /**
   * Закрытие текущего preview-редактора (смена файла / dialog / unmount).
   * Dirty → awaiting_ws; иначе restore preOpen.
   */
  function closeActivePreviewEditor(): { awaitingWs: boolean; file: FileItem | null } {
    const active = activePreviewEditor
    activePreviewEditor = null
    if (!active) {
      attachmentR7Preview.destroyEditor()
      clearInlinePreviewHostDom()
      return { awaitingWs: false, file: null }
    }
    const result = finalizeR7AttachmentEditorClose({
      documentKey: active.documentKey,
      item: active.item,
      elementId: active.elementId,
    })
    attachmentR7Preview.destroyEditor()
    clearInlinePreviewHostDom()
    return { awaitingWs: result.awaitingWs, file: active.item }
  }

  function enterAwaitingSaveOverlay(file: FileItem): void {
    clearPreviewFocusDebounce()
    clearPreviewRestoreDebounce()
    bumpPreviewGeneration()
    activePreviewEditor = null
    attachmentR7Preview.destroyEditor()
    clearInlinePreviewHostDom()
    previewLoading.value = true
    previewError.value = null
    previewLastFocusFile.value = file
  }

  function suspendForEditor(): void {
    clearPreviewFocusDebounce()
    clearPreviewRestoreDebounce()
    bumpPreviewGeneration()
    // Переход в dialog: не finalize/awaiting_ws — правки продолжаются в модалке.
    // Новая session зарегистрируется в openAttachment; prior row session сбросится.
    activePreviewEditor = null
    attachmentR7Preview.destroyEditor()
    clearInlinePreviewHostDom()
    previewLoading.value = false
  }

  async function runInlinePreview(file: FileItem): Promise<void> {
    if (isEditorVisible.value) return

    const resolved = fileAttachmentsElement.resolveAttachmentItemFromStore(file)
    const previewElementId = resolvePreviewElementId()
    if (isAttachmentR7PreviewAwaitingFinalSave(resolved, previewElementId)) {
      enterAwaitingSaveOverlay(resolved)
      return
    }
    if (!fileAttachmentsElement.canPreviewR7InlineForFile(resolved)) return

    const snapshot =
      previewElementId != null ? getDsAttachmentStore(previewElementId).getSnapshot(resolved) : undefined
    const dsPhase = snapshot?.phase
    const reuseStagedPreviewUri = dsPhase !== 'awaiting_ws'
    const stagingStorageUri = reuseStagedPreviewUri ? snapshot?.storageUri : undefined
    const stagingDocumentSessionId = reuseStagedPreviewUri ? snapshot?.documentSessionId : undefined

    bumpPreviewGeneration()
    const gen = previewLoadGen
    previewAbortController = new AbortController()
    const signal = previewAbortController.signal
    previewLoading.value = true
    previewError.value = null
    previewLastFocusFile.value = resolved

    const ctx = fileAttachmentsElement.resolveR7EditorOpenContext()
    if (!ctx) {
      if (gen !== previewLoadGen) return
      previewLoading.value = false
      previewError.value = 'Не удалось загрузить просмотр: нет sessionId или elementId.'
      return
    }

    let openedDocumentKey = ''

    try {
      await nextTick()
      if (gen !== previewLoadGen || isEditorVisible.value || signal.aborted) return
      const socketId = (await wsService.waitForSocketId(5000)) ?? wsService.getSocketId()
      if (gen !== previewLoadGen || isEditorVisible.value || signal.aborted) return
      const handle = await attachmentR7Preview.openAttachmentPreview(
        {
          item: resolved,
          elementId: ctx.elementId,
          elementName: ctx.elementName,
          session: ctx.session,
          socketId,
          mode: fileAttachmentsElement.resolveR7EditorMode(resolved),
        },
        {
          stagingStorageUri,
          stagingDocumentSessionId,
          onStagingMetaPersist: (item, meta) => {
            fileAttachmentsElement.persistStagingMetaForFile(item, meta)
          },
          onPrepared: (result) => {
            openedDocumentKey = result.documentKey
            activePreviewEditor = {
              item: resolved,
              elementId: ctx.elementId,
              documentKey: result.documentKey,
            }
          },
          onEditorEvent: (name, payload) => {
            if (name === 'onDocumentStateChange' && payload === true) {
              markR7AttachmentDocumentDirty({
                item: resolved,
                elementId: ctx.elementId,
                documentKey: openedDocumentKey || activePreviewEditor?.documentKey,
              })
            }
            if (name === 'onRequestClose') {
              const { awaitingWs, file: closedFile } = closeActivePreviewEditor()
              if (awaitingWs && closedFile) {
                enterAwaitingSaveOverlay(closedFile)
              }
            }
          },
          signal,
          shouldAbort: () =>
            gen !== previewLoadGen
            || isEditorVisible.value
            || signal.aborted
            || isAttachmentR7PreviewAwaitingFinalSave(
              fileAttachmentsElement.resolveAttachmentItemFromStore(file),
              previewElementId,
            ),
        },
      )
      if (handle.documentKey) {
        openedDocumentKey = handle.documentKey
        activePreviewEditor = {
          item: resolved,
          elementId: ctx.elementId,
          documentKey: handle.documentKey,
        }
      }
      if (gen !== previewLoadGen) {
        // Поколение сменилось — close уже сделает следующий run / suspend.
        attachmentR7Preview.destroyEditor()
        clearInlinePreviewHostDom()
        if (activePreviewEditor?.documentKey === handle.documentKey) {
          activePreviewEditor = null
        }
        return
      }
    } catch (error) {
      if (gen !== previewLoadGen || isAbortError(error)) return
      const axiosMsg = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message
      const message =
        axiosMsg
        || (error instanceof Error ? error.message : 'Не удалось загрузить просмотр документа.')
      previewError.value = message
      closeActivePreviewEditor()
    } finally {
      if (
        gen === previewLoadGen
        && !isAttachmentR7PreviewAwaitingFinalSave(
          fileAttachmentsElement.resolveAttachmentItemFromStore(file),
          previewElementId,
        )
      ) {
        previewLoading.value = false
      }
    }
  }

  /**
   * Смена выбранного файла на ленте (office / pdf / image / null).
   * Всегда финализирует предыдущий R7 preview (dirty → awaiting_ws), затем открывает office inline при необходимости.
   */
  function handleTapePreviewFocus(file: FileItem | null): void {
    if (isEditorVisible.value) return

    if (!file) {
      closeActivePreviewEditor()
      clearPreviewFocusDebounce()
      bumpPreviewGeneration()
      lastTapePreviewFocusKey = ''
      lastTapePreviewFocusFile = null
      previewLastFocusFile.value = null
      previewLoading.value = false
      return
    }

    const resolved = fileAttachmentsElement.resolveAttachmentItemFromStore(file)
    const focusKey = attachmentUiRowKey(resolved)

    // Rebind той же строки (новый object ref / client→code:version) — no-op.
    // Важно: до close/bump/clear debounce, иначе второй emit сорвёт запланированный open.
    if (
      lastTapePreviewFocusFile
      && attachmentItemsReferSameRow(lastTapePreviewFocusFile, resolved)
    ) {
      lastTapePreviewFocusKey = focusKey
      lastTapePreviewFocusFile = resolved
      previewLastFocusFile.value = resolved
      return
    }
    if (focusKey === lastTapePreviewFocusKey) {
      previewLastFocusFile.value = resolved
      return
    }

    closeActivePreviewEditor()
    clearPreviewFocusDebounce()
    bumpPreviewGeneration()

    previewLastFocusFile.value = resolved

    if (isPreviewAwaitingForFile(resolved)) {
      lastTapePreviewFocusKey = ''
      lastTapePreviewFocusFile = null
      enterAwaitingSaveOverlay(resolved)
      return
    }

    if (!fileAttachmentsElement.canPreviewR7InlineForFile(resolved)) {
      lastTapePreviewFocusKey = ''
      lastTapePreviewFocusFile = null
      previewLoading.value = false
      return
    }

    lastTapePreviewFocusKey = focusKey
    lastTapePreviewFocusFile = resolved

    previewFocusDebounceTimer = setTimeout(() => {
      previewFocusDebounceTimer = null
      void runInlinePreview(resolved)
    }, 150)
  }

  function handleStagingUpdated(payload: R7AttachmentStagingUpdatedPayload): void {
    if (isEditorVisible.value) return
    const file = previewLastFocusFile.value
    if (!file) return
    const resolved = fileAttachmentsElement.resolveAttachmentItemFromStore(file)
    const previewElementId = resolvePreviewElementId()
    if (isAttachmentR7PreviewAwaitingFinalSave(resolved, previewElementId)) {
      enterAwaitingSaveOverlay(resolved)
      return
    }
    const cv = resolveAttachmentRowCodeVersion((resolved.fields ?? {}) as Record<string, unknown>)
    if (payload.code > 0) {
      if (!cv || cv.code !== payload.code) return
    } else {
      if (!isNewLocalAttachmentItem(resolved)) return
      const focusName = resolveAttachmentDisplayFileName(file)
      if (resolveAttachmentDisplayFileName(resolved) !== focusName) return
    }
    previewError.value = null
    previewLoading.value = false
    clearPreviewRestoreDebounce()

    if (!fileAttachmentsElement.canPreviewR7InlineForFile(resolved)) {
      return
    }

    if (formSaveHoldPreview) return

    // Фокус на этой строке + staged: открыть inline preview (после awaiting_ws из dialog/preview).
    scheduleRestore(resolved)
  }

  function scheduleRestore(overrideFile?: FileItem): void {
    if (formSaveHoldPreview) return
    const file = overrideFile ?? previewLastFocusFile.value
    if (!file || isEditorVisible.value) return
    const resolved = fileAttachmentsElement.resolveAttachmentItemFromStore(file)
    previewLastFocusFile.value = resolved
    if (isPreviewAwaitingForFile(resolved)) {
      enterAwaitingSaveOverlay(resolved)
      return
    }
    clearPreviewRestoreDebounce()
    previewError.value = null
    previewRestoreDebounceTimer = setTimeout(() => {
      previewRestoreDebounceTimer = null
      if (formSaveHoldPreview || isEditorVisible.value) return
      const current = previewLastFocusFile.value
      if (!current) return
      const currentResolved = fileAttachmentsElement.resolveAttachmentItemFromStore(current)
      previewLastFocusFile.value = currentResolved
      if (isPreviewAwaitingForFile(currentResolved)) {
        enterAwaitingSaveOverlay(currentResolved)
        return
      }
      void runInlinePreview(currentResolved)
    }, 150)
  }

  function onEditorClosedAwaitingWs(file: FileItem): void {
    enterAwaitingSaveOverlay(file)
  }

  function closeEditorsForFormSave(): void {
    if (isEditorVisible.value) return
    if (!activePreviewEditor) return
    const session = getEditingSession(activePreviewEditor.documentKey)
    if (session?.phase === 'staged') return
    if (session?.wasDirty && session.editorMode !== 'view') return
    closeActivePreviewEditor()
    clearPreviewFocusDebounce()
    clearPreviewRestoreDebounce()
    bumpPreviewGeneration()
    previewLoading.value = false
  }

  function destroyPreviewKeepStaged(): void {
    if (!activePreviewEditor) return
    markEditorClosedKeepStaged(activePreviewEditor.documentKey)
    activePreviewEditor = null
    attachmentR7Preview.destroyEditor()
    clearInlinePreviewHostDom()
    clearPreviewFocusDebounce()
    clearPreviewRestoreDebounce()
    bumpPreviewGeneration()
    previewLoading.value = false
  }

  async function waitForPreviewFormSave(): Promise<R7FormSaveWaitResult> {
    const active = activePreviewEditor
    const session = active ? getEditingSession(active.documentKey) : undefined
    const invoked = await fileAttachmentsElement.requestOfficeEditorForceSave(
      session?.documentSessionId,
    )
    if (!invoked) {
      fileAttachmentsElement.abortIfR7FormSaveWaitNotReady('error')
      return 'error'
    }
    return fileAttachmentsElement.waitUntilR7NotBlockingGetFullData()
  }

  function beginFormSaveHold(): void {
    formSaveHoldPreview = true
    clearPreviewRestoreDebounce()
  }

  function endFormSaveHold(): void {
    formSaveHoldPreview = false
  }

  async function prepareForFormSave(): Promise<void> {
    beginFormSaveHold()
    const previewReady = await waitUntilAttachmentPreviewReadyForFormSave({
      isPreviewLoading: () => previewLoading.value,
      hasUploadableNativeFile: () => fileAttachmentsElement.hasUploadableNativeFileForFormSave(),
    })
    fileAttachmentsElement.abortIfR7FormSaveWaitNotReady(previewReady)
    const active = activePreviewEditor
    const sessionBefore = active ? getEditingSession(active.documentKey) : undefined
    const dirtyOpen = Boolean(
      sessionBefore && sessionBefore.editorMode !== 'view' && sessionBefore.wasDirty,
    )
    if (dirtyOpen) {
      const waitResult = await waitForPreviewFormSave()
      const after = activePreviewEditor
        ? getEditingSession(activePreviewEditor.documentKey)
        : undefined
      if (shouldDestroyR7EditorAfterFormSaveWait(waitResult, after) && activePreviewEditor) {
        destroyPreviewKeepStaged()
      }
      fileAttachmentsElement.abortIfR7FormSaveWaitNotReady(waitResult)
      return
    }
    if (activePreviewEditor) {
      closeActivePreviewEditor()
      clearPreviewFocusDebounce()
      clearPreviewRestoreDebounce()
      bumpPreviewGeneration()
      previewLoading.value = false
    }
    const waitResult = await fileAttachmentsElement.waitUntilR7NotBlockingGetFullData()
    fileAttachmentsElement.abortIfR7FormSaveWaitNotReady(waitResult)
  }

  function dispose(): void {
    clearPreviewFocusDebounce()
    clearPreviewRestoreDebounce()
    bumpPreviewGeneration()
    closeActivePreviewEditor()
    previewLoading.value = false
  }

  onUnmounted(() => {
    dispose()
  })

  return {
    previewLoading,
    previewError,
    inlinePreviewOverlay,
    handleTapePreviewFocus,
    handleStagingUpdated,
    suspendForEditor,
    scheduleRestore,
    onEditorClosedAwaitingWs,
    closeEditorsForFormSave,
    beginFormSaveHold,
    endFormSaveHold,
    prepareForFormSave,
    dispose,
  }
}
